import { useState } from 'react'
import { Button, IconButton, Input, Spinner, Typography } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import { PiEyeBold, PiEyeSlashBold } from 'react-icons/pi'
import { useGeneralToast } from '../hooks/toast'
import { usePageTitle } from '../hooks/toolkit'
import { updateUserPassword } from '../utils/helpers/supabase.helpers'
import { useDarkMode } from '../contexts/DarkModeContext'

const UserSetNewPassword = () => {
  const { t } = useTranslation()
  usePageTitle('Set new password | Agami')

  const { success: toastSuccess, error: toastError } = useGeneralToast()

  const [password, setPassword] = useState<string>('')
  const [passwordConfirm, setpasswordConfirm] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const { darkMode } = useDarkMode()

  const updateUserData = async (e: React.FormEvent<HTMLFormElement>) => {
    setLoading(true)
    e.preventDefault()
    e.stopPropagation()

    const { error: updateUserPasswordError } = await updateUserPassword({
      password,
      passwordConfirm,
    })

    if (updateUserPasswordError) {
      toastError((updateUserPasswordError as Error)?.message ?? String(updateUserPasswordError))
      setLoading(false)
      return
    }

    setLoading(false)
    toastSuccess(t('password.passwordSetSuccess'))
  }

  return (
    <div className="w-11/12 mx-auto mt-12">
      <Typography variant="h1" className="text-primary dark:text-base-gray-400">
        {t('password.title')}
      </Typography>

      <Typography variant="h5" className="text-primary dark:text-base-gray-400">
        {t('password.subtitle')}
      </Typography>

      <form className="w-full md:w-1/2 flex flex-col gap-4 mt-8" onSubmit={updateUserData}>
        <div>
          <div className="relative flex w-full">
            <Input
              className="pr-20"
              value={password || ''}
              onChange={(e) => setPassword(e.currentTarget.value)}
              type={showPassword ? 'text' : 'password'}
              label={t('password.newPassword')}
              required
              data-testid="profile-new-password-input"
              {...(darkMode && { color: 'white' })}
              containerProps={{
                className: 'min-w-0',
              }}
            />
            <IconButton
              size="sm"
              variant="text"
              className="!absolute right-1 top-1 rounded text-main dark:text-white"
              onClick={() => setShowPassword((prev) => !prev)}
            >
              {showPassword ? <PiEyeSlashBold /> : <PiEyeBold />}
            </IconButton>
          </div>

          <Typography variant="small" className="mt-1 dark:text-gray-300">
            {t('password.passwordRequirements')}
          </Typography>
        </div>

        <div className="relative flex w-full">
          <Input
            className="pr-20"
            value={passwordConfirm || ''}
            onChange={(e) => setpasswordConfirm(e.currentTarget.value)}
            type={showPassword ? 'text' : 'password'}
            label={t('password.confirmPassword')}
            required
            data-testid="profile-confirm-password-input"
            {...(darkMode && { color: 'white' })}
            containerProps={{
              className: 'min-w-0',
            }}
          />
          <IconButton
            size="sm"
            variant="text"
            className="!absolute right-1 top-1 rounded text-main dark:text-white"
            onClick={() => setShowPassword((prev) => !prev)}
          >
            {showPassword ? <PiEyeSlashBold /> : <PiEyeBold />}
          </IconButton>
        </div>

        <Button type="submit" disabled={loading} data-testid="profile-save-password-btn">
          {loading ? <Spinner className="h-4 w-4 mx-auto" /> : t('password.saveNewPassword')}
        </Button>
      </form>
    </div>
  )
}

export default UserSetNewPassword
